import React, { Component, ReactNode } from 'react';
import { InAppBrowserWarning } from './InAppBrowserWarning';
import { Card } from '@/components/ui/card';

interface Props {
  children: ReactNode;
}

interface State {
  hasWebKitError: boolean;
  error: Error | null;
}

/**
 * Error boundary that catches webkit.messageHandlers errors
 * (common in Facebook/Instagram in-app browsers) and shows
 * a helpful message instead of crashing
 */
export class WebKitErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasWebKitError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State | null {
    try {
      // Safely extract error message
      let errorMessage = '';
      try {
        errorMessage = error?.message || error?.toString() || '';
      } catch (e) {
        // If getting message throws, treat as non-webkit error
        return null;
      }

      // Check if this is a webkit-related error
      const isWebKitError =
        errorMessage.includes('webkit.messageHandlers') ||
        errorMessage.includes('window.webkit') ||
        errorMessage.includes('messageHandlers');

      if (isWebKitError) {
        return {
          hasWebKitError: true,
          error,
        };
      }

      // For non-webkit errors, return null to let parent error boundaries handle them
      // DO NOT throw here as it causes infinite recursion!
      return null;
    } catch (boundaryError) {
      // CRITICAL: If error boundary itself throws, prevent infinite recursion
      console.error(
        'Error in WebKitErrorBoundary.getDerivedStateFromError:',
        boundaryError
      );
      // Return null to let parent handle it
      return null;
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    try {
      // Safely extract error message
      let errorMessage = '';
      try {
        errorMessage = error?.message || error?.toString() || '';
      } catch (e) {
        console.error('Failed to extract error message in componentDidCatch');
        return;
      }

      const isWebKitError =
        errorMessage.includes('webkit.messageHandlers') ||
        errorMessage.includes('window.webkit') ||
        errorMessage.includes('messageHandlers');

      if (isWebKitError) {
        console.warn('WebKit compatibility error caught:', error, errorInfo);
        // Report to Sentry but mark as handled
        try {
          if (window.Sentry) {
            window.Sentry.captureException(error, {
              contexts: {
                react: {
                  componentStack: errorInfo.componentStack,
                },
              },
              tags: {
                error_boundary: 'webkit',
                handled: 'true',
              },
            });
          }
        } catch (sentryError) {
          console.error('Failed to report to Sentry:', sentryError);
        }
      } else {
        // Let non-webkit errors bubble up to parent error boundaries
        // We only log them here for debugging
        console.error(
          'Non-webkit error in WebKitErrorBoundary:',
          error,
          errorInfo
        );
      }
    } catch (catchError) {
      // CRITICAL: Prevent infinite recursion if componentDidCatch itself throws
      console.error(
        'Error in WebKitErrorBoundary.componentDidCatch:',
        catchError
      );
    }
  }

  render() {
    if (this.state.hasWebKitError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                Learn German
              </h1>
              <p className="text-muted-foreground mt-2">
                Welcome to your language learning journey
              </p>
            </div>

            <Card className="bg-card/50 backdrop-blur-sm border-primary/20 p-6">
              <InAppBrowserWarning forceShow={true} />

              <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground text-center">
                  Don't worry - your data is safe. Just open this page in your
                  phone's regular browser to continue.
                </p>
              </div>
            </Card>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Add Sentry type to window
declare global {
  interface Window {
    Sentry?: any;
  }
}
